import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  Qualification,
  AdvanceQuestionResponse,
  SearchAdvanceQuestionsPayload,
  Category,
} from '../../../shared/interfaces/qualification.interface';
import { QUALIFICATION_CODES } from '../../../shared/constants/qualification-code';
import { EnvironmentConfig } from '../../../shared/interfaces/environment.interface';
import { Constants } from '../../../shared/constants/create-survey.constants';
import { deepCopy } from '../../utils/deep-copy';

@Injectable({
  providedIn: 'root',
})
export class QualificationsService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;
  private _qualifications: Qualification[] = [];

  constructor(
    private _http: HttpClient,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  getUIQualifications(
    countryCode: string = 'US',
    languageCode: string = 'en',
    isMcPreDraftCase: boolean = false,
    isRecontactSurvey: boolean = false,
    extraCoreQualification: boolean = false
  ): Observable<Qualification[]> {
    /**
     * For multi-country surveys, we show only default criteria - Gender, Age, Device, etc.,
     * independent of the countries selected. Hence using default locale to fetch question data to avoid
     * scenarios where for the asked locale, we do not have data available
     */
    return this._http
      .get<Qualification[]>(
        `${this._baseUrl}/buyers/v3/attributes/ui/${countryCode}/${languageCode}`
      )
      .pipe(
        map((qualifications: Qualification[]) => {
          this._qualifications = qualifications;
          if (isMcPreDraftCase) {
            return qualifications.filter((qualification: Qualification) =>
              [
                QUALIFICATION_CODES.GENDER,
                QUALIFICATION_CODES.AGE,
                QUALIFICATION_CODES.DEVICE,
              ].includes(qualification.qualification_code)
            );
          } else if (isRecontactSurvey) {
            return qualifications.filter((qualification: Qualification) =>
              [QUALIFICATION_CODES.DEVICE].includes(
                qualification.qualification_code
              )
            );
          } else if (
            countryCode === Constants.AUSTRALIA_SHORT_CODE &&
            !extraCoreQualification
          ) {
            return qualifications.filter(
              (qualification) =>
                ![QUALIFICATION_CODES.RELATIONSHIP].includes(
                  qualification.qualification_code
                )
            );
          } else {
            // PD-7510 - Hiding Race qualification
            return qualifications.filter(
              (qualification: Qualification) =>
                ![QUALIFICATION_CODES.RACE].includes(
                  qualification.qualification_code
                )
            );
          }
        })
      );
  }

  getQualifications() {
    return deepCopy(this._qualifications);
  }

  getQuestions(payload: SearchAdvanceQuestionsPayload) {
    return this._http.post<AdvanceQuestionResponse>(
      `${this._baseUrl}/survey/search_adv_questions`,
      payload
    );
  }

  getCategories() {
    const url = `${this._baseUrl}/buyers/v3/attributes/categories/all`;
    return this._http.get<Category[]>(url);
  }
}
